// MARK:- OS 2.0 web component for tab-based sections (gift-boxes, featured-recipe, etc.)
//
// File naming: standalone web components in this theme use the `component-` prefix
// in the FILE NAME (`assets/component-tabs.js`, `assets/component-accordion.js`, …)
// so they sort together in the assets directory and are easy to grep. The custom
// element name, JS class, and data attributes stay short and natural.
//
// Architecture: this file is a hand-edited asset, NOT a webpack/gulp build output.
// Sections that need tabs include it via:
//
//   <script src="{{ 'component-tabs.js' | asset_url }}" type="module"></script>
//
// `type="module"` loads + executes the file once per page regardless of how many
// section instances reference it, so multiple instances of the same section share
// one load. Shopify's CDN handles minification + cache headers — no local build step.
//
// Markup contract (prefixed attributes to avoid colliding with master's legacy
// jQuery `handleTabs` in src/js/theme.js, which uses unprefixed data-tab/-content/-hover):
//
//   <as-tabs class="…">
//     <a data-as-tab="block-id" data-as-tab-hover>…</a>
//     <div data-as-tab-content="block-id">…</div>
//   </as-tabs>
//
// Behavior:
// - First [data-as-tab] is active on connect.
// - Click on a [data-as-tab] activates it; matching [data-as-tab-content] gets is-active.
// - Hover on [data-as-tab-hover] activates after an 80ms tolerance (no hover-on-pass).
// - Touch devices ((hover: none) and (pointer: coarse)) lose href on hover triggers
//   so taps switch tabs instead of navigating away. Original href is preserved on
//   dataset.asTabSavedHref. Users navigate via in-tab Shop CTAs (kept visible on
//   mobile via `.hover-change--device-both` in master's CSS).

class AsTabs extends HTMLElement {
  connectedCallback() {
    AsTabs.ensureBaseStyle();
    this.hoverDelay = 80;
    this.hoverTimer = null;
    this.bindEvents();
    this.activateInitial();
    this.neutralizeHrefsOnTouch();
  }

  static ensureBaseStyle() {
    if (document.querySelector('style[data-as-tabs-base]')) return;
    const style = document.createElement('style');
    style.setAttribute('data-as-tabs-base', '');
    style.textContent = 'as-tabs { display: block; }';
    document.head.appendChild(style);
  }

  bindEvents() {
    this.addEventListener('click', (event) => {
      const trigger = event.target.closest('[data-as-tab]');
      if (!trigger || !this.contains(trigger)) return;
      this.activate(trigger.dataset.asTab);
    });

    this.addEventListener('mouseover', (event) => {
      const trigger = event.target.closest('[data-as-tab-hover][data-as-tab]');
      if (!trigger || !this.contains(trigger)) return;
      if (event.relatedTarget && trigger.contains(event.relatedTarget)) return;
      clearTimeout(this.hoverTimer);
      this.hoverTimer = setTimeout(() => {
        this.classList.add('is-hovered');
        this.activate(trigger.dataset.asTab);
      }, this.hoverDelay);
    });

    this.addEventListener('mouseout', (event) => {
      const trigger = event.target.closest('[data-as-tab-hover][data-as-tab]');
      if (!trigger) return;
      if (event.relatedTarget && trigger.contains(event.relatedTarget)) return;
      clearTimeout(this.hoverTimer);
    });
  }

  activateInitial() {
    const firstTrigger = this.querySelector('[data-as-tab]');
    if (firstTrigger) this.activate(firstTrigger.dataset.asTab);
  }

  activate(tabId) {
    if (!tabId) return;
    this.querySelectorAll('[data-as-tab]').forEach((trigger) => {
      trigger.classList.toggle('is-active', trigger.dataset.asTab === tabId);
    });
    this.querySelectorAll('[data-as-tab-content]').forEach((content) => {
      content.classList.toggle('is-active', content.dataset.asTabContent === tabId);
    });
  }

  neutralizeHrefsOnTouch() {
    const noHover = window.matchMedia('(hover: none) and (pointer: coarse)').matches;
    if (!noHover) return;

    this.querySelectorAll('a[data-as-tab][data-as-tab-hover]').forEach((link) => {
      if (link.dataset.asTabSavedHref) return;
      const href = link.getAttribute('href');
      if (!href) return;
      link.dataset.asTabSavedHref = href;
      link.removeAttribute('href');
    });
  }
}

if (!customElements.get('as-tabs')) {
  customElements.define('as-tabs', AsTabs);
}
